import {
    assertAutomaticPresentationCoordinatorOptions,
    compareAutomaticPresentationRecords,
    getDuplicateAutomaticPresentationIds,
    normalizeAutomaticPresentationConditions,
    normalizeAutomaticPresentationOptions,
    type NormalizedAutomaticPresentationOptions,
} from './coordinatorUtils';
import {
    type AutomaticPresentationBlocker,
    type AutomaticPresentationCandidateReason,
    type AutomaticPresentationCandidateSnapshot,
    type AutomaticPresentationCloseReason,
    type AutomaticPresentationConditions,
    type AutomaticPresentationCoordinator,
    type AutomaticPresentationCoordinatorSnapshot,
    type AutomaticPresentationOptions,
    type AutomaticPresentationRegistration,
    type CreateAutomaticPresentationCoordinatorOptions,
} from './types';

type RegistrationToken = number;
type BlockerToken = number;

type RegistrationRecord = {
    token: RegistrationToken;
    registrationId: string;
    options: NormalizedAutomaticPresentationOptions;
    routeGeneration: number | null;
    isInWaitEpisode: boolean;
    hasWaitTimedOut: boolean;
    waitTimerId: ReturnType<typeof setTimeout> | null;
};

type CurrentPresentation = {
    token: RegistrationToken;
    phase: 'active' | 'leaving';
    closeReason?: AutomaticPresentationCloseReason;
};

type TerminalCloseReason = Exclude<AutomaticPresentationCloseReason, 'cancelled'>;

/** Результат выбора: либо готовый победитель, либо более важный кандидат, который ещё ждёт готовности. */
type SelectionResult = Readonly<{
    winnerToken: RegistrationToken | null;
    blockingToken: RegistrationToken | null;
}>;

// Координатор разрешает только один автоматический показ и определяет, кто получит слот следующим.
// Условия показа, содержимое, отрисовка и сохранение результата остаются в модуле-владельце.
class AutomaticPresentationCoordinatorImpl implements AutomaticPresentationCoordinator {
    private readonly registrations = new Map<RegistrationToken, RegistrationRecord>();
    private readonly blockers = new Map<BlockerToken, string>();
    /** Не даёт завершённому показу открыться снова, пока владелец не начнёт новый цикл через `ineligible`. */
    private readonly suppressedPresentationIds = new Map<string, TerminalCloseReason>();
    private readonly listeners = new Set<() => void>();
    private readonly waitingCandidateTimeoutMs: number;

    private nextRegistrationToken = 1;
    private nextBlockerToken = 1;
    private routeGeneration = 0;
    private isRouteSettled = true;
    private currentPresentation: CurrentPresentation | null = null;
    private revision = 0;
    private isReconciliationScheduled = false;
    private isDestroyed = false;
    private snapshot: AutomaticPresentationCoordinatorSnapshot = {
        revision: 0,
        route: { generation: 0, isSettled: true },
        current: null,
        queue: [],
        blockers: [],
        candidates: [],
    };

    constructor({ waitingCandidateTimeoutMs }: CreateAutomaticPresentationCoordinatorOptions) {
        this.waitingCandidateTimeoutMs = waitingCandidateTimeoutMs;
    }

    register = (options: AutomaticPresentationOptions): AutomaticPresentationRegistration => {
        this.assertNotDestroyed();

        const normalizedOptions = normalizeAutomaticPresentationOptions(options);
        const token = this.nextRegistrationToken++;
        const record: RegistrationRecord = {
            token,
            registrationId: `registration-${token}`,
            options: normalizedOptions,
            routeGeneration: normalizedOptions.scope === 'route' ? this.routeGeneration : null,
            isInWaitEpisode: false,
            hasWaitTimedOut: false,
            waitTimerId: null,
        };

        if (normalizedOptions.eligibility === 'ineligible') {
            this.suppressedPresentationIds.delete(normalizedOptions.id);
        }

        this.registrations.set(token, record);
        this.syncCandidateWaitTimer(record);
        this.scheduleReconciliation();

        return {
            update: (conditions) => this.updateRegistration(token, conditions),
            unregister: () => this.unregister(token),
            startLeaving: (reason) => this.startLeaving(token, reason),
            completeLeaving: () => this.completeLeaving(token),
            isActive: () => this.currentPresentation?.token === token && this.currentPresentation.phase === 'active',
            isLeaving: () => this.currentPresentation?.token === token && this.currentPresentation.phase === 'leaving',
        };
    };

    acquireBlocker = (id: string): AutomaticPresentationBlocker => {
        this.assertNotDestroyed();

        if (id.trim().length === 0) {
            throw new Error('Идентификатор блокировки автоматических показов не должен быть пустым');
        }

        const token = this.nextBlockerToken++;
        let isReleased = false;

        this.blockers.set(token, id);

        // Открываемая внешняя поверхность должна сразу стать единственной видимой, поэтому разрешение
        // отзывается и во время анимации закрытия. Сама блокировка не считается пользовательским закрытием.
        this.currentPresentation = null;

        this.scheduleReconciliation();

        return {
            release: () => {
                if (isReleased) {
                    return;
                }

                isReleased = true;

                if (this.blockers.delete(token)) {
                    this.scheduleReconciliation();
                }
            },
        };
    };

    beginRouteTransition = (): number => {
        this.assertNotDestroyed();

        this.routeGeneration += 1;
        this.isRouteSettled = false;

        // Удаляем регистрации старой страницы сразу:
        // React-компоненты могут размонтироваться уже после начала перехода.
        this.registrations.forEach((record, token) => {
            if (record.options.scope === 'route') {
                this.deleteRegistration(token);
            }
        });

        this.scheduleReconciliation();

        return this.routeGeneration;
    };

    completeRouteTransition = (generation: number): boolean => {
        if (this.isDestroyed || generation !== this.routeGeneration || this.isRouteSettled) {
            return false;
        }

        this.isRouteSettled = true;
        this.scheduleReconciliation();

        return true;
    };

    getSnapshot = (): AutomaticPresentationCoordinatorSnapshot => this.snapshot;

    subscribe = (listener: () => void): (() => void) => {
        if (this.isDestroyed) {
            return () => undefined;
        }

        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    };

    destroy = (): void => {
        if (this.isDestroyed) {
            return;
        }

        this.isDestroyed = true;
        this.isReconciliationScheduled = false;
        this.registrations.forEach((record) => this.resetCandidateWait(record));
        this.registrations.clear();
        this.blockers.clear();
        this.suppressedPresentationIds.clear();
        this.currentPresentation = null;
        this.listeners.clear();
    };

    private assertNotDestroyed(): void {
        if (this.isDestroyed) {
            throw new Error('Координатор автоматических показов уже уничтожен');
        }
    }

    private updateRegistration(token: RegistrationToken, conditions: AutomaticPresentationConditions): void {
        const record = this.registrations.get(token);

        if (!record || this.isDestroyed) {
            return;
        }

        const normalizedConditions = normalizeAutomaticPresentationConditions(conditions);

        if (
            record.options.eligibility === normalizedConditions.eligibility &&
            record.options.isReady === normalizedConditions.isReady
        ) {
            return;
        }

        if (normalizedConditions.eligibility === 'ineligible') {
            this.suppressedPresentationIds.delete(record.options.id);
        }

        // Правила планирования фиксируются при регистрации:
        // `update` не может случайно сменить `priority`, `order` или `scope`.
        record.options = {
            ...record.options,
            eligibility: normalizedConditions.eligibility,
            isReady: normalizedConditions.isReady,
        };

        this.syncCandidateWaitTimer(record);
        this.scheduleReconciliation();
    }

    private unregister(token: RegistrationToken): void {
        if (this.isDestroyed) {
            return;
        }

        if (this.deleteRegistration(token)) {
            this.scheduleReconciliation();
        }
    }

    private deleteRegistration(token: RegistrationToken): boolean {
        const record = this.registrations.get(token);

        if (!record) {
            return false;
        }

        this.resetCandidateWait(record);
        this.registrations.delete(token);

        if (this.currentPresentation?.token === token) {
            this.currentPresentation = null;
        }

        return true;
    }

    private startLeaving(token: RegistrationToken, reason: AutomaticPresentationCloseReason): boolean {
        if (
            this.isDestroyed ||
            this.currentPresentation?.token !== token ||
            this.currentPresentation.phase !== 'active'
        ) {
            return false;
        }

        const record = this.registrations.get(token);

        if (!record) {
            return false;
        }

        if (reason !== 'cancelled') {
            this.suppressedPresentationIds.set(record.options.id, reason);
            this.resetCandidateWait(record);
        }

        // Слот остаётся занятым до конца анимации, чтобы следующий показ не появился поверх закрывающегося.
        this.currentPresentation = { token, phase: 'leaving', closeReason: reason };
        this.scheduleReconciliation();

        return true;
    }

    private completeLeaving(token: RegistrationToken): boolean {
        if (
            this.isDestroyed ||
            this.currentPresentation?.token !== token ||
            this.currentPresentation.phase !== 'leaving'
        ) {
            return false;
        }

        this.currentPresentation = null;
        this.scheduleReconciliation();

        return true;
    }

    private syncCandidateWaitTimer(record: RegistrationRecord): void {
        const isSuppressed = this.suppressedPresentationIds.has(record.options.id);
        const shouldWait =
            !isSuppressed &&
            (record.options.eligibility === 'pending' ||
                (record.options.eligibility === 'eligible' && !record.options.isReady));

        // Ожидание данных и затем элемента на странице считается одной попыткой показа
        // и не перезапускает таймаут.
        if (shouldWait === record.isInWaitEpisode) {
            return;
        }

        this.resetCandidateWait(record);

        if (!shouldWait) {
            return;
        }

        record.isInWaitEpisode = true;
        record.waitTimerId = setTimeout(() => {
            const currentRecord = this.registrations.get(record.token);

            // Старый таймер не должен повлиять на новую регистрацию,
            // даже если она использует тот же логический `id`.
            if (this.isDestroyed || currentRecord !== record || !record.isInWaitEpisode) {
                return;
            }

            record.waitTimerId = null;
            record.hasWaitTimedOut = true;
            this.scheduleReconciliation();
        }, this.waitingCandidateTimeoutMs);
    }

    private resetCandidateWait(record: RegistrationRecord): void {
        if (record.waitTimerId !== null) {
            clearTimeout(record.waitTimerId);
        }

        record.waitTimerId = null;
        record.isInWaitEpisode = false;
        record.hasWaitTimedOut = false;
    }

    private scheduleReconciliation(): void {
        if (this.isDestroyed || this.isReconciliationScheduled) {
            return;
        }

        this.isReconciliationScheduled = true;

        // Откладываем пересчёт до конца текущей операции: синхронно созданные регистрации сравниваются вместе,
        // поэтому победитель определяется правилами очереди, а не порядком запуска React-эффектов.
        void Promise.resolve().then(() => {
            if (this.isDestroyed) {
                return;
            }

            this.isReconciliationScheduled = false;
            this.reconcile();
        });
    }

    private reconcile(): void {
        const records = [...this.registrations.values()].sort(compareAutomaticPresentationRecords);
        const duplicateIds = getDuplicateAutomaticPresentationIds(records);

        this.releaseInvalidCurrentPresentation(duplicateIds);

        let selection: SelectionResult = { winnerToken: null, blockingToken: null };

        if (!this.currentPresentation && this.blockers.size === 0 && this.isRouteSettled) {
            selection = this.selectNextPresentation(records, duplicateIds);

            if (selection.winnerToken !== null) {
                this.currentPresentation = { token: selection.winnerToken, phase: 'active' };
            }
        }

        this.revision += 1;
        this.snapshot = this.buildSnapshot(records, duplicateIds, selection.blockingToken);
        [...this.listeners].forEach((listener) => listener());
    }

    private releaseInvalidCurrentPresentation(duplicateIds: ReadonlySet<string>): void {
        if (!this.currentPresentation) {
            return;
        }

        const record = this.registrations.get(this.currentPresentation.token);

        if (!record) {
            this.currentPresentation = null;

            return;
        }

        // Закрывающийся показ сам освобождает слот через completeLeaving после своей анимации.
        if (this.currentPresentation.phase === 'leaving') {
            return;
        }

        // Более важный кандидат влияет только на следующий выбор и не прерывает уже начавшийся показ.
        const isInvalid =
            this.blockers.size > 0 ||
            duplicateIds.has(record.options.id) ||
            this.suppressedPresentationIds.has(record.options.id) ||
            record.options.eligibility !== 'eligible' ||
            !record.options.isReady ||
            (record.options.scope === 'route' && record.routeGeneration !== this.routeGeneration);

        if (isInvalid) {
            this.currentPresentation = null;
        }
    }

    private selectNextPresentation(
        records: readonly RegistrationRecord[],
        duplicateIds: ReadonlySet<string>
    ): SelectionResult {
        for (const record of records) {
            if (
                duplicateIds.has(record.options.id) ||
                this.suppressedPresentationIds.has(record.options.id) ||
                record.options.eligibility === 'ineligible' ||
                record.hasWaitTimedOut ||
                (record.options.scope === 'route' && record.routeGeneration !== this.routeGeneration)
            ) {
                continue;
            }

            // Первый по очереди кандидат ждёт готовности до таймаута и не пропускает вперёд менее важные показы.
            if (record.options.eligibility === 'pending' || !record.options.isReady) {
                return { winnerToken: null, blockingToken: record.token };
            }

            return { winnerToken: record.token, blockingToken: null };
        }

        return { winnerToken: null, blockingToken: null };
    }

    private buildSnapshot(
        records: readonly RegistrationRecord[],
        duplicateIds: ReadonlySet<string>,
        blockingToken: RegistrationToken | null
    ): AutomaticPresentationCoordinatorSnapshot {
        const currentRecord = this.currentPresentation
            ? this.registrations.get(this.currentPresentation.token)
            : undefined;
        const blockingRecord = blockingToken === null ? undefined : this.registrations.get(blockingToken);
        const queue = records
            .filter(
                (record) =>
                    record.token !== this.currentPresentation?.token &&
                    !duplicateIds.has(record.options.id) &&
                    !this.suppressedPresentationIds.has(record.options.id) &&
                    record.options.eligibility === 'eligible' &&
                    record.options.isReady &&
                    !record.hasWaitTimedOut
            )
            .map(({ options }) => options.id);

        return {
            revision: this.revision,
            route: {
                generation: this.routeGeneration,
                isSettled: this.isRouteSettled,
            },
            current:
                this.currentPresentation && currentRecord
                    ? {
                          registrationId: currentRecord.registrationId,
                          id: currentRecord.options.id,
                          phase: this.currentPresentation.phase,
                          ...(this.currentPresentation.closeReason
                              ? { closeReason: this.currentPresentation.closeReason }
                              : {}),
                      }
                    : null,
            queue,
            blockers: [...this.blockers.values()].sort(),
            candidates: records.map((record) =>
                this.buildCandidateSnapshot(record, duplicateIds, blockingRecord, currentRecord)
            ),
        };
    }

    private buildCandidateSnapshot(
        record: RegistrationRecord,
        duplicateIds: ReadonlySet<string>,
        blockingRecord: RegistrationRecord | undefined,
        currentRecord: RegistrationRecord | undefined
    ): AutomaticPresentationCandidateSnapshot {
        const { options } = record;
        const baseSnapshot = {
            registrationId: record.registrationId,
            id: options.id,
            priority: options.priority,
            order: options.order,
            scope: options.scope,
            routeGeneration: record.routeGeneration,
            eligibility: options.eligibility,
            isReady: options.isReady,
            ...(options.debug ? { debug: options.debug } : {}),
        };

        if (this.currentPresentation?.token === record.token) {
            return {
                ...baseSnapshot,
                state: this.currentPresentation.phase,
                ...(this.currentPresentation.closeReason && this.currentPresentation.closeReason !== 'cancelled'
                    ? { suppressedBy: this.currentPresentation.closeReason }
                    : {}),
            };
        }

        if (duplicateIds.has(options.id)) {
            return { ...baseSnapshot, state: 'duplicate', reason: 'duplicate-id' };
        }

        const suppressedBy = this.suppressedPresentationIds.get(options.id);

        if (suppressedBy) {
            return { ...baseSnapshot, state: 'suppressed', suppressedBy };
        }

        if (options.eligibility === 'ineligible') {
            return { ...baseSnapshot, state: 'ineligible' };
        }

        if (record.hasWaitTimedOut) {
            return { ...baseSnapshot, state: 'timed-out', reason: 'wait-timeout' };
        }

        if (options.eligibility === 'pending') {
            return { ...baseSnapshot, state: 'pending' };
        }

        if (!options.isReady) {
            return { ...baseSnapshot, state: 'waiting-target' };
        }

        const blocked = this.getReadyCandidateBlock(record, blockingRecord, currentRecord);

        if (blocked) {
            return { ...baseSnapshot, ...blocked };
        }

        return { ...baseSnapshot, state: 'queued' };
    }

    private getReadyCandidateBlock(
        record: RegistrationRecord,
        blockingRecord: RegistrationRecord | undefined,
        currentRecord: RegistrationRecord | undefined
    ):
        | Readonly<{
              state: 'blocked' | 'queued';
              reason: AutomaticPresentationCandidateReason;
              blockedBy?: string;
          }>
        | undefined {
        if (this.blockers.size > 0) {
            return { state: 'blocked', reason: 'external-blocker' };
        }

        if (currentRecord) {
            return { state: 'queued', reason: 'current-presentation', blockedBy: currentRecord.options.id };
        }

        if (!this.isRouteSettled) {
            return { state: 'blocked', reason: 'route-unsettled' };
        }

        if (blockingRecord && compareAutomaticPresentationRecords(blockingRecord, record) < 0) {
            return {
                state: 'blocked',
                reason: 'higher-ranked-waiting',
                blockedBy: blockingRecord.options.id,
            };
        }

        return undefined;
    }
}

export function createAutomaticPresentationCoordinator(
    options: CreateAutomaticPresentationCoordinatorOptions
): AutomaticPresentationCoordinator {
    assertAutomaticPresentationCoordinatorOptions(options);

    return new AutomaticPresentationCoordinatorImpl(options);
}
